import classnames from 'classnames';
import moment from 'moment';
import React from 'react';
import {Link} from 'react-router-dom';
import {
  Column,
  Filters,
  Row,
  SortingRule,
  useFilters,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';

import {ApiProjectReport} from 'app/modules/Remote/Feature';
import * as routeUtils from 'app/utils/routeUtils';

import cs from '../PropertyOverview.module.scss';
import {InputWrapper} from '../PropertyTable';
import useFiltersPatch from '../useFiltersPatch';
import {
  AuthorColumnFilter,
  CreatedColumnFilter,
  PropertyColumnFilter,
  TypeColumnFilter,
} from './PortfolioReportsColumnFilters';
import {
  AUTHOR_COLUMN_ID,
  CREATED_COLUMN_ID,
  PROPERTY_COLUMN_ID,
  SELECTION_COLUMN_ID,
  TITLE_COLUMN_ID,
  TYPE_COLUMN_ID,
  UPDATED_COLUMN_ID,
  dateRangeBounds,
  decodeDateRange,
  decodeIdList,
  reportDocumentType,
  reportMatchesSearch,
} from './reportsTableState';
import ownCs from './styles.module.scss';

const DISPLAY_DATE_FORMAT = 'MM/DD/YYYY';

// Date columns hold epoch milliseconds so sorting and range filtering don't
// re-parse ISO strings per row.
const formatDate = (ms: number) => moment(ms).format(DISPLAY_DATE_FORMAT);

export function usePortfolioReportsTable({
  reports,
  organizationId,
  projectId,
  initialFilters,
  initialSortBy,
}: {
  reports: ApiProjectReport[];
  organizationId: string;
  projectId: string;
  initialFilters: Filters<ApiProjectReport>;
  initialSortBy: SortingRule<ApiProjectReport>[];
}) {
  const columns = React.useMemo<Column<ApiProjectReport>[]>(
    () => [
      {
        id: SELECTION_COLUMN_ID,
        className: cs.cellSelect,
        Header: ({isAllRowsSelected, toggleAllRowsSelected, selectedFlatRows}) => (
          <div className={classnames(cs.selectionInputWrapper, ownCs.selectionCell)}>
            <InputWrapper
              onChange={() => toggleAllRowsSelected()}
              state={
                isAllRowsSelected
                  ? 'checked'
                  : selectedFlatRows.length
                    ? 'indeterminate'
                    : 'unchecked'
              }
            />
          </div>
        ),
        Cell: ({row}) => (
          <div className={classnames(cs.selectionInputWrapper, ownCs.selectionCell)}>
            <InputWrapper
              tabIndex={-1}
              onChange={() => row.toggleRowSelected(!row.isSelected)}
              state={row.isSelected ? 'checked' : 'unchecked'}
            />
          </div>
        ),
      },
      {
        id: TITLE_COLUMN_ID,
        Header: 'Document',
        accessor: 'title',
        Filter: () => null,
        filter: (rows: Row<ApiProjectReport>[], _, filterValue: string) =>
          rows.filter((r) => reportMatchesSearch(r.original, filterValue || '')),
        Cell: ({row, cell: {value}}) => (
          <Link
            to={routeUtils.makeSavedReportUrl(
              organizationId,
              projectId,
              row.original.feature.lensId,
              row.original.id
            )}
          >
            {value}
          </Link>
        ),
      },
      {
        id: TYPE_COLUMN_ID,
        Header: 'Type',
        accessor: reportDocumentType,
        Filter: TypeColumnFilter,
        filter: (rows: Row<ApiProjectReport>[], [columnId], filterValue: string) =>
          filterValue ? rows.filter((r) => r.values[columnId] === filterValue) : rows,
      },
      {
        id: PROPERTY_COLUMN_ID,
        Header: 'Property',
        accessor: (report) => report.feature.displayName,
        Filter: PropertyColumnFilter,
        filter: (rows: Row<ApiProjectReport>[], _, filterValue: string) => {
          const lensIds = decodeIdList(filterValue);
          return lensIds.length
            ? rows.filter((r) => lensIds.includes(r.original.feature.lensId))
            : rows;
        },
        Cell: ({row, cell: {value}}) => (
          <Link
            to={routeUtils.makeProjectDashboardUrl(organizationId, projectId, 'map', [
              row.original.feature.lensId,
            ])}
          >
            {value}
          </Link>
        ),
      },
      {
        id: AUTHOR_COLUMN_ID,
        Header: 'Author',
        accessor: (report) => report.createdByUser?.name || report.createdByUser?.email || '',
        Filter: AuthorColumnFilter,
        filter: (rows: Row<ApiProjectReport>[], _, filterValue: string) => {
          const userIds = decodeIdList(filterValue);
          return userIds.length ? rows.filter((r) => userIds.includes(r.original.createdBy)) : rows;
        },
      },
      {
        id: CREATED_COLUMN_ID,
        Header: 'Created',
        accessor: (report) => Date.parse(report.createdAt),
        sortType: 'basic',
        Filter: CreatedColumnFilter,
        filter: (rows: Row<ApiProjectReport>[], [columnId], filterValue: string) => {
          const {startMs, endMs} = dateRangeBounds(decodeDateRange(filterValue));
          return rows.filter((r) => r.values[columnId] >= startMs && r.values[columnId] <= endMs);
        },
        Cell: ({cell: {value}}) => formatDate(value),
      },
      {
        id: UPDATED_COLUMN_ID,
        Header: 'Updated',
        accessor: (report) => Date.parse(report.updatedAt),
        sortType: 'basic',
        Cell: ({row, cell: {value}}) => {
          const report = row.original;
          if (report.updatedAt === report.createdAt) {
            return null;
          }
          return (
            <>
              {formatDate(value)}
              {report.updatedByUser && (
                <span className={ownCs.updatedBy}> by {report.updatedByUser.name}</span>
              )}
            </>
          );
        },
      },
    ],
    [organizationId, projectId]
  );

  const table = useTable(
    {
      columns,
      data: reports,
      getRowId: (report) => String(report.id),
      autoResetFilters: false,
      autoResetSortBy: false,
      autoResetSelectedRows: false,
      initialState: {filters: initialFilters, sortBy: initialSortBy},
    },
    useFilters,
    useFiltersPatch,
    useSortBy,
    useRowSelect
  );

  return {table};
}
